import React from "react";
import SaveWithMetaData from "../../src/taskpane/components/directoryComponents/sharepoint/SaveToSharepoint";
import { useDispatch, useSelector } from "react-redux";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { SharePointInputData } from "../../src/taskpane/interfaces/props/SharePointInputData";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import savingLocationStructureModule from "../../utilities/SavingLocationStructureModule";
import DuplicateFiles from "../../src/taskpane/components/directoryComponents/oneDrive/DuplicateFiles";
import { IPanelStyles, IStackItemStyles, IStackStyles, IStackTokens, MessageBar, MessageBarButton, MessageBarType, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import { resetFiles, setOneDriveOverwriteOptionSelected } from "../../src/taskpane/redux/modules/file";
import { setSavingLocation } from "../../src/taskpane/redux/modules/savingLocation";
import { Slide, ToastContainer } from "react-toastify";
import { DatasourceValidationHelper } from "../../src/taskpane/components/helpers/datasourceValidationHelper";
import { setDatasourceValues } from "../../src/taskpane/redux/modules/datasource";
import { useFileUpload } from "../Body/useFileUpload";
import { LanguageContext } from "../../utilities/LocalizationModule";
import generalModule from "../../utilities/GeneralModule";
import { FileOption } from "../../src/taskpane/interfaces/IFileOption";
import { updateIndividualFileOptionsValue } from "../../src/taskpane/components/helpers/metaDataHelper";
import { logger } from "../../utilities/logger";
import { VersionSelectorMinMajOnly } from "../../src/taskpane/components/_v2/_shared/VersionSelector/VersionSelectorMinMajOnly";
import authModule from "../../utilities/AuthModule";
import { SharePointFolderContentTypeIds, sharePointInternalFileName } from "../../src/taskpane/config/constants";
import { OneDriveOverWriteEnum } from "../../src/taskpane/components/enums/oneDriveOverWriteEnum";
import DuplicateFolders from "../../src/taskpane/components/directoryComponents/oneDrive/DuplicateFolders";
import { BulkMetadataUpdateRequest, MetadataDto, UpdateTriggerAction } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { getListSettings } from "../../src/taskpane/services/SharePointRestServices";
import { getItem } from "../../src/taskpane/services/GraphService";
import { apiBulkUpdateItemMetadata } from "../../src/taskpane/services/ApiService";
import { refrehOptionsRequest } from "../../src/taskpane/config/datasourceConstants";
import { DatasourceHelper } from "../../src/taskpane/components/helpers/dataSourceHelper";
import { useClientTypeContext } from "../../src/taskpane/components/hooks/useClientTypeContext";

const panelStyles: Partial<IPanelStyles> = {
    main: {
        overflow: "hidden"
    },
    content: {
        paddingLeft: 5,
        paddingRight: 5,
    },
    header: {
        marginBottom: 10,
        paddingLeft: 15
    },
    footer: {
        backgroundColor: "var(--db-bg, #fff)"
    },
    commands: {
        backgroundColor: "var(--db-bg, #fff)"
    }
}

const containerStackTokens: IStackTokens = {
    childrenGap: 5
};

const containerStackStyles: IStackStyles = {
    root: {
        height: "100%",
    }
};

const metadataStackItemStyles: IStackItemStyles = {
    root: {
        overflowY: "auto"
    }
};

export interface ICMFilesUpdatePanel {
    closePanel?: () => void;
    reloadGrid?: () => void;
    panelIsOpen: boolean;
    selectedItems?: any[];
}

export const CMFilesUpdatePanel = ({ closePanel, reloadGrid, panelIsOpen, selectedItems }: ICMFilesUpdatePanel) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const clientTypes = useClientTypeContext(currentDirectory?.siteId);
    const datasourceValues = useSelector((state: RootState) => state.datasource.values);
    const datasourceInitialLoad = useSelector((state: RootState) => state.datasource.initialLoad);
    const datasourceCallerId = useSelector((state: RootState) => state.datasource?.callerId);
    const datasourceItems = useSelector((state: RootState) => state.tenantInfo.datasources);
    const canOverwrite = useSelector((state: RootState) => state.files.canOverwrite);
    const siteInfo = useSelector((state: RootState) => state.tenantInfo?.sites);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const moveTo = useSelector((state: RootState) => state.contentManagerDirectory.moveTo);
    const tenantId = useSelector((state: RootState) => state.user?.account?.tenantId);
    const accountId = useSelector((state: RootState) => state.user?.account?.accountId);

    const [files, setFilesLocal] = React.useState(selectedItems);
    const [inputValuesToSave, setInputValuesToSave] = React.useState<SharePointInputData[]>([]);
    const [filesUploaded, setFilesUploaded] = React.useState(files?.length > 0);
    const [isCurrentDirectorySPList, setIsCurrentDirectorySPList] = React.useState<boolean>(false);
    const [isBusy, setIsBusy] = React.useState<boolean>(false);
    const [showErrorMessage, setShowErrorMessage] = React.useState<boolean>(false);

    const [_fileOptions, _setFileOptions] = React.useState<FileOption[]>([]);
    const fileOptionsRef = React.useRef<FileOption[]>([]);
    const setFileOptions = (fileOptions: FileOption[]) => {
        fileOptionsRef.current = fileOptions;
        _setFileOptions(fileOptions);
    };

    const removeMessage = React.useCallback(() => setShowErrorMessage(false), []);

    const sharedContentType = React.useMemo(() => {
        if (Array.isArray(selectedItems)
            && selectedItems.length > 0
            && selectedItems.every(item => item?.ContentTypeId === selectedItems[0]?.ContentTypeId)) {
            return selectedItems[0]?.ContentTypeId;
        }
        return null;
    }, [selectedItems]);

    const isInOneDrive = React.useRef(currentDirectory.type == ComponentTypes.OneDrive || currentDirectory.type == ComponentTypes.OneDriveSubFolder);

    const { showSelectVersion, setShowSelectVersion, setSelectedCheckinType, spHasDuplicateFiles, setSpHasDuplicateFiles, spHasDuplicateFolders, setSpHasDuplicateFolders } = useFileUpload();

    React.useEffect(() => {
        const workingDirectoriesAvailable: boolean = Array.isArray(workingDirectories) && workingDirectories.length > 0;
        setIsCurrentDirectorySPList(workingDirectoriesAvailable && workingDirectories.some(x => x.type === ComponentTypes.SharePointList));
    }, [workingDirectories, siteInfo]);

    React.useEffect(() => {
        dispatch(setOneDriveOverwriteOptionSelected(OneDriveOverWriteEnum.notSelected));
    }, []);

    React.useEffect(() => {
        if (!generalModule.getIsMultiMetaFileUpdatePanelOpen() && datasourceValues) {
            updateMetadataPropertiesFromDataSource(datasourceValues);
        }
    }, [datasourceValues]);

    React.useEffect(() => {
        generalModule.setIsMultiMetaFileUpdatePanelOpen(false);

        savingLocationStructureModule.setSavingLocationStructure(workingDirectories);
        dispatch(
            setSavingLocation({
                display: currentDirectory?.displayName,
                webUrl: currentDirectory?.webUrl,
                directory: currentDirectory?.directory,
                teamId: workingDirectories?.find(dir => dir.teamGroupId)?.teamGroupId,
                channelId: workingDirectories?.find(dir => dir.teamChannelId)?.teamChannelId
            }, isInOneDrive.current)
        );
    }, []);

    React.useEffect(() => {
        if (fileOptionsRef.current?.length > 0) {
            const updateFileOptions = fileOptionsRef.current.map(fileOption => {
                return {
                    ...fileOption,
                    canOverwrite
                }
            })

            setFileOptions(updateFileOptions);
        }
    }, [canOverwrite]);

    const updateMetadataPropertiesFromDataSource = (values: any) => {
        const isMultipleValues: boolean = Array.isArray(values) && values.length > 0;
        const propertyKeys = isMultipleValues
            ? Object.keys(values[0])
            : Object.keys(values);

        if (propertyKeys.length > 0) {
            const updatedinputValuesToSave = [...inputValuesToSave];
            propertyKeys.forEach((propKey) => {
                let updatedValue: any;
                if (isMultipleValues) {
                    const uniqueValues = values
                        .map(value => value[propKey])
                        .filter((value, index, self) => self.indexOf(value) === index);
                    updatedValue = uniqueValues;
                }
                else {
                    updatedValue = values[propKey];
                }
                updateDatasourceDisplayNameInputData(updatedinputValuesToSave, propKey, updatedValue, isMultipleValues);
            });
            updateSharepointMetadataToSave(updatedinputValuesToSave, null);
        }
        dispatch(setDatasourceValues(null));
    };

    const updateDatasourceDisplayNameInputData = (
        updatedinputValuesToSave: SharePointInputData[],
        propKey: string,
        value: any,
        isMultipleValues: boolean
    ) => {
        let inputDatasourceId: number;
        let inputToUpdate = inputValuesToSave.find(
            (item) => {
                const boundName: string = item.displayName?.trim()?.toLowerCase();
                if (boundName && item.internalName !== sharePointInternalFileName && boundName === propKey.trim().toLowerCase()) {
                    inputDatasourceId = datasourceCallerId && DatasourceHelper.getInputDataSource(datasourceItems, boundName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""], clientTypes)?.id;
                    return true;
                }
                return false;
            }
        );

        if (inputToUpdate) {
            const inputDatasourceCompatible: boolean = Number.isInteger(inputDatasourceId) && inputDatasourceId === datasourceCallerId;
            if (value === refrehOptionsRequest) {
                const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
                if (updatedInput) {
                    updatedInput.resetDatasourceOptionsToggle = !updatedInput.resetDatasourceOptionsToggle;
                }
            }
            else if (isMultipleValues || inputDatasourceCompatible) { //value param is an array
                try {
                    const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
                    if (updatedInput) {
                        if (!Array.isArray(value)) {
                            value = [value];
                        }
                        if (updatedInput.value) {
                            if (Array.isArray(updatedInput.value) && updatedInput.value.length > 0) {
                                updatedInput.value = updatedInput.value
                                    .map(val => {
                                        if (val && value.some(x => x?.trim().toLowerCase() === val.trim().toLowerCase())) {
                                            return val;
                                        }
                                        return null;
                                    })
                                    .filter(val => val);
                            }
                            else if (!datasourceInitialLoad) {
                                if (value.length === 1) {
                                    DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
                                }
                                else if ((value.length > 1 && !value.some(x => x?.trim().toLowerCase() === updatedInput?.value?.trim().toLowerCase()))) {
                                    updatedInput.value = null;
                                }
                            }
                        }
                        else if (value.length === 1) {
                            DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
                        }
                        updatedInput.updatedDatasourceOptions = value;
                    }
                } catch (error) {
                    logger(error, "CMFilesUpdatePanel.tsx (updateDatasourceDisplayNameInputData)");
                }
            }
            else {
                DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value);
            }
        }
    };

    const onPanelCLose = () => {
        if (!moveTo.moveToEnabled) {
            dispatch(resetFiles([]));
        }
        closePanel();
    }

    const updateSharepointMetadataToSave = (metadata: SharePointInputData[], informationProtectionId: string, changedIndex?: number) => {
        setInputValuesToSave(metadata);

        if (fileOptionsRef.current?.length > 0) {
            setFileOptions(updateIndividualFileOptionsValue(fileOptionsRef.current, metadata, changedIndex, languageStrings.TitleAutoSet));
        }
    };

    const getItemWithExistingGraphId = async (driveId, itemId) => {
        const fileItem = await getItem(
            await authModule.getGraphAccessToken(),
            driveId,
            itemId
        )
        if (fileItem) {
            return fileItem?.sharepointIds?.listItemUniqueId
        } else {
            return null
        }
    };

    const processSelectedItems = async (items) => {
        const itemIds = await Promise.all(items.map(async (item) => {
            if (item.hasGraphItemId === true) {
                return await getItemWithExistingGraphId(item?.driveId, item.id);
            } else if ('UniqueId' in item && item.UniqueId) {
                return item.UniqueId.replace('{', '').replace('}', '');
            } else if ('id' in item) {
                return item.id;
            }
            return null;
        }));

        return itemIds.filter(id => id !== null);
    }

    const updateFilesSplit = async () => {
        const filesArray = files?.filter((item) => !item?.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder));
        const documentSetsArray = files?.filter((item) => item?.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet));

        setIsBusy(true);

        const [filesResponse, foldersResponse] = await Promise.all([
            Array.isArray(filesArray) && filesArray.length > 0 ? bulkUpdateItems(filesArray) : true,
            Array.isArray(documentSetsArray) && documentSetsArray.length > 0 ? bulkUpdateItems(documentSetsArray, true) : true
        ]);

        if (filesResponse && foldersResponse) {
            reloadGrid();
            closePanel();
        }
        else {
            setShowErrorMessage(true);
        }

        setIsBusy(false);
    }

    const bulkUpdateItems = async (items: any[], isFolder: boolean = false): Promise<boolean> => {
        try {
            const accessToken = isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken();

            let currentSharepointFolderInfo;
            const webUrl = currentDirectory?.webUrl;
            const listId = currentDirectory?.sharePointListId;
            if (webUrl && listId) {
                const [settings] = await Promise.all([
                    getListSettings(accessToken, webUrl, listId)
                ]);

                currentSharepointFolderInfo = {
                    sharepointListId: listId,
                    canView: false,
                    canCreateFolder: settings ? settings.EnableFolderCreation : currentSharepointFolder?.canCreateFolder ?? false,
                    contentTypesEnabled: settings ? settings.ContentTypesEnabled : currentSharepointFolder?.contentTypesEnabled ?? false,
                    enableMinorVersions: settings ? settings.EnableMinorVersions : currentSharepointFolder?.enableMinorVersions ?? false,
                };
            }

            const request: BulkMetadataUpdateRequest = {
                accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                tenantGuid: tenantId,
                accountId,
                siteUrl: webUrl,
                listId: listId,
                teamId: workingDirectories?.find(dir => dir.teamGroupId)?.teamGroupId,
                channelId: workingDirectories?.find(dir => dir.teamChannelId)?.teamChannelId,
                listItemUniqueIds: await processSelectedItems(items),
                isFolder: isFolder,
                isListItem: isCurrentDirectorySPList ? true : false,
                listSettings: {
                    enableMinorVersions: currentSharepointFolderInfo?.enableMinorVersions === true,
                },
                triggeredByAction: UpdateTriggerAction.Default,
                metadata: inputValuesToSave
                    .map(input => {
                        const meta: MetadataDto = {
                            type: input.type,
                            internalName: input.internalName,
                            value: !(input?.value === 0) && (!input?.value || (Array.isArray(input?.value) && !input?.value?.length)) ? undefined : input.value
                        }
                        return meta;
                    })
                    .filter(meta => meta.value)
            }

            await apiBulkUpdateItemMetadata(request);
            return true;
        } catch (error) {
            logger(error, "CMFilesUpdatePanel.tsx (ApiBulkUpdate)");
            return false;
        }
    }

    const cancelDuplicateAndVersionOptionSelection = () => {
        setShowSelectVersion(false);
        setSpHasDuplicateFiles(false);
        setSpHasDuplicateFolders(false);
    }

    const resetIndividualFileInputValues = () => {
        setFileOptions([]);
    }

    const onRenderFooterContent = React.useCallback(
        () => (
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
                <PrimaryButton
                    iconProps={{ iconName: "save" }}
                    disabled={isBusy}
                    onClick={() => {
                        updateFilesSplit();
                    }}
                    text={isBusy ? languageStrings.SaveInProgress : languageStrings.SaveProperties}
                >
                    {isBusy && <Spinner size={SpinnerSize.small} styles={{ root: { marginLeft: 5 } }} />}
                </PrimaryButton>
            </Stack>
        ),
        [isBusy, inputValuesToSave],
    );

    return (
        <Panel
            headerText={languageStrings.EditXItems.replace("{itemCount}", files?.length.toString())}
            isOpen={panelIsOpen}
            onDismiss={() => onPanelCLose()}
            isLightDismiss={false}
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={'350px'}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            <>
                <Stack tokens={containerStackTokens} styles={containerStackStyles}>
                    {files?.length > 1 && _fileOptions?.some(x => x.isModified) && <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline={false}
                        dismissButtonAriaLabel="Close"
                        actions={
                            <div>
                                <MessageBarButton onClick={() => {
                                    resetIndividualFileInputValues();
                                }}>{languageStrings.Reset}</MessageBarButton>
                            </div>
                        }
                    >{languageStrings.IndividualFilesUpdated}</MessageBar>}
                    <Stack.Item grow styles={metadataStackItemStyles}>
                        {(showSelectVersion || spHasDuplicateFiles || spHasDuplicateFolders) && <div className="xb-content-lite">
                            {spHasDuplicateFolders && !spHasDuplicateFiles && <DuplicateFolders showSelectVersion={showSelectVersion} onCancel={cancelDuplicateAndVersionOptionSelection} />}
                            {spHasDuplicateFiles && <DuplicateFiles showSelectVersion={showSelectVersion} onCancel={cancelDuplicateAndVersionOptionSelection} hasDuplicateFolders={spHasDuplicateFolders} />}
                            {showSelectVersion && <VersionSelectorMinMajOnly checkinTypeChanged={setSelectedCheckinType} onCancel={cancelDuplicateAndVersionOptionSelection} labelFontSize={16} paddingTop={0} paddingLeft={0} />}
                        </div>}
                        <div className={(showSelectVersion || spHasDuplicateFiles || spHasDuplicateFolders) ? "none" : "xb-content"}>
                            {((moveTo.moveToEnabled || isCurrentDirectorySPList || filesUploaded) && !isInOneDrive.current) && <SaveWithMetaData
                                isBulkEdit={true}
                                itemContentTypeId={sharedContentType}
                                addNameField={false}
                                inputValuesToSave={inputValuesToSave}
                                updateSharepointMetadataToSave={updateSharepointMetadataToSave}
                                isCurrentDirectorySPList={isCurrentDirectorySPList}
                            ></SaveWithMetaData>}
                        </div>
                    </Stack.Item>
                    <ToastContainer autoClose={5000} hideProgressBar transition={Slide}></ToastContainer>
                    {showErrorMessage && <React.Fragment>
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            onDismiss={removeMessage}
                            dismissButtonAriaLabel="Close"
                            truncated={true}
                        >
                            {languageStrings.ErrorWhileUpdatingMetadata}
                        </MessageBar>
                    </React.Fragment>}
                </Stack>
            </>
        </Panel>
    )
}